import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Heading from '../../components/Text/Heading';
import { StyledForm, StyledInitialDataContent } from './styles';
import FormGroup from '../../components/Form/FormGroup';
import { useCallback, useEffect, useState } from 'react';
import { modelService, templateService, legalAreaService, sectionService } from '../../api/services';
import { OptionType, Select } from '../../components/Form/Select';
import Button from '../../components/Form/Button';
import Paragraph from '../../components/Text/Paragraph';
import BlankDocument from '../../assets/icons/blank_document.svg';
import Input from '../../components/Form/Input';
import { PagedModelDataRequest } from '../../types/Model.types';
import useDebounce from '../../hooks/useDebounce';
import { LegalAreaData } from '../../types/LegalArea.types';
import { SectionData } from '../../types/Section.types';

export interface InitialDataFormValues {
  name: string;
  arealegal: OptionType;
  secao: OptionType;
  model: OptionType;
  template: OptionType;
  client: string;
  folder: string;
  clientName: string;
}

interface InitialDataFormProps {
  initialData?: InitialDataFormValues;
  onSubmit: (data: InitialDataFormValues) => void;
  onBack: () => void;
  loading: boolean;
}

const validationSchema = yup.object({
  client: yup
    .number()
    .required('O campo cliente é obrigatório')
    .positive('O valor do cliente deve ser positivo')
    .typeError('O campo cliente é obrigatório'),
  folder: yup
    .number()
    .required('O campo pasta é obrigatório')
    .positive('O valor da pasta deve ser positivo')
    .typeError('O campo pasta é obrigatório'),
  arealegal: yup.object().shape({
    label: yup.string().required('O campo área juridica é obrigatório'),
    value: yup.string().required(),
  }),
  secao: yup.object().shape({
    label: yup.string().required('O campo seção é obrigatório'),
    value: yup.string().required(),
  }),
  model: yup.object().shape({
    label: yup.string().required('O campo modelo é obrigatório'),
    value: yup.string().required(),
  }),
  template: yup.object().shape({
    label: yup.string().required('O campo template é obrigatório'),
    value: yup.string().required(),
  }),
});

export const InitialDataForm = ({ initialData, onSubmit, onBack, loading }: InitialDataFormProps) => {
  const [models, setModels] = useState<OptionType[]>([]);
  const [arealegal, setArealegal] = useState<OptionType[]>([]);
  const [secao, setSecao] = useState<OptionType[]>([]);
  const [templates, setTemplates] = useState<OptionType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [fieldVisibility, setFieldVisibility] = useState<boolean[]>(Array(6).fill(false));
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    register,
    formState: { errors },
  } = useForm<InitialDataFormValues>({
    defaultValues: initialData,
    resolver: yupResolver(validationSchema),
  });

  const secaoData = watch('secao')?.value;
  const areaLegalData = watch('arealegal')?.value;

  const fetchModels = useCallback(
    async (search: string) => {
      try {
        const requestData: PagedModelDataRequest = {
          pagination: { skip: 0, limit: 50 },
          sort: { field: 'dataCriacao', order: 'DESC' },
          query: search,
          section: secaoData,
          areaLegal: areaLegalData,
          activeOnly: true,
        };
        const result = await modelService.listPaged(requestData);

        if (result && result.data.length > 0) {
          const models = result.data.map((model: any) => ({
            label: model.nomeModelo as string,
            value: model.idModelo as string,
          }));

          setModels(models);
          //setValue('model', { label: '', value: '' });
          return models;
        } else {
          setModels([]);
        }
        return [];
      } catch (error) {
        console.error('Erro ao buscar modelos:', error);
        return [];
      }
    },
    [secaoData, areaLegalData]
  );

  const fetchAreaLegal = useCallback(async () => {
    try {
      const result = await legalAreaService.list();
      if (result) {
        const arealegal = result.map((areaJuridica: LegalAreaData) => ({
          label: areaJuridica.nomeAreaJuridica as string,
          value: areaJuridica.idAreaJuridica as string,
        }));

        if (arealegal.length > 0) {
          // Verifica se há elementos no array
          setArealegal(arealegal);
          return arealegal;
        }
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar áreas jurídicas:', error);
      return [];
    }
  }, []);

  const fetchTemplates = async () => {
    try {
      const templatesResponse = await templateService.list();
      setTemplates(
        templatesResponse?.map((template) => ({
          label: template.nomeTemplate as string,
          value: template.idTemplate as string,
        })) || []
      );
    } catch (error) {
      console.error('Erro ao buscar templates:', error);
    }
  };

  const fetchSecao = useCallback(async () => {
    try {
      const result = await sectionService.list();
      if (result) {
        const secao = result.map((value: SectionData) => ({
          label: value.tituloSecao as string,
          value: value.idSecao as string,
        }));

        if (secao.length > 0) {
          // Verifica se há elementos no array
          setSecao(secao);
          return secao;
        }
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar seção:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    // if (debouncedSearchTerm) {
    fetchModels(debouncedSearchTerm);
    // }
  }, [debouncedSearchTerm, fetchModels]);

  useEffect(() => {
    fetchTemplates();
    fetchAreaLegal();
    fetchSecao();

    setFieldVisibility((prev) => {
      const newVisibility = [...prev];
      newVisibility[0] = true;
      return newVisibility;
    });
  }, []);

  const handleChange = useCallback(
    (field: any, index: number) => (selectedOption: any) => {
      field.onChange(selectedOption);

      setFieldVisibility((prev) => {
        const newVisibility = [...prev];
        if (selectedOption) {
          newVisibility[index + 1] = true;
        }
        return newVisibility;
      });
    },
    []
  );

  const handleInputChange = useCallback((inputValue: string) => {
    setSearchTerm(inputValue);
  }, []);

  const renderSelectAreaLegal = useCallback(
    ({ field }: any) => (
      <>
        <Paragraph className="label">Área jurídica</Paragraph>
        <Select
          placeholder="Selecionar área jurídica"
          options={arealegal}
          onChange={handleChange(field, 0)} // Passa o índice 0 para ativar o próximo campo
        />
      </>
    ),
    [arealegal, handleChange]
  );

  const renderSelectSecao = useCallback(
    ({ field }: any) => (
      <>
        <Paragraph className="label">Seção</Paragraph>
        <Select placeholder="Selecionar seção" options={secao} onChange={handleChange(field, 1)} />
      </>
    ),
    [secao, handleChange]
  );

  const renderSelectModels = useCallback(
    ({ field }: any) => (
      <>
        <Paragraph className="label">Modelo</Paragraph>
        {field?.value?.value === '' ? (
          <Select
            placeholder="Pesquisar modelo"
            options={models}
            onInputChange={handleInputChange}
            onChange={handleChange(field, 2)}
          />
        ) : (
          <Select
            {...field}
            placeholder="Pesquisar modelo"
            options={models}
            onInputChange={handleInputChange}
            onChange={handleChange(field, 2)}
          />
        )}
      </>
    ),
    [models, handleInputChange, handleChange]
  );

  const renderSelectTemplates = useCallback(
    ({ field }: any) => (
      <>
        <Paragraph className="label">Template</Paragraph>
        <Select placeholder="Escolha o template" options={[...templates]} onChange={handleChange(field, 3)} />
      </>
    ),
    [templates, handleChange]
  );

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <div>
        <Heading>Para começar nos informe alguns dados:</Heading>

        {fieldVisibility[0] && (
          <FormGroup error={errors.arealegal?.label?.message}>
            <Controller name="arealegal" control={control} render={renderSelectAreaLegal} />
          </FormGroup>
        )}

        {fieldVisibility[1] && (
          <FormGroup error={errors.secao?.label?.message}>
            <Controller name="secao" control={control} render={renderSelectSecao} />
          </FormGroup>
        )}

        {fieldVisibility[2] && (
          <FormGroup error={errors.model?.label?.message}>
            <Controller name="model" control={control} render={renderSelectModels} />
          </FormGroup>
        )}

        {fieldVisibility[3] && (
          <FormGroup error={errors.template?.label?.message}>
            <Controller name="template" control={control} render={renderSelectTemplates} />
          </FormGroup>
        )}

        {fieldVisibility[4] && (
          <FormGroup error={errors.client?.message}>
            <Input {...register('client')} label="Código do cliente" type="number" />
          </FormGroup>
        )}

        {fieldVisibility[4] && (
          <FormGroup error={errors.folder?.message}>
            <Input {...register('folder')} label="Código da pasta" type="number" />
          </FormGroup>
        )}
      </div>

      <div className="buttons">
        <Button type="button" kind="secondary" onClick={onBack}>
          Voltar
        </Button>

        <Button type="submit" disabled={loading}>
          {loading ? 'Aguarde...' : 'Próximo'}
        </Button>
      </div>
    </StyledForm>
  );
};

export const InitialDataContent = () => (
  <StyledInitialDataContent>
    <img src={BlankDocument} />
    <Heading size="small">DOCUMENTO EM BRANCO</Heading>
    <Paragraph>Você ainda não começou o preenchimento do documento.</Paragraph>
  </StyledInitialDataContent>
);
